import React from 'react';

import collaborationImage from 'assets/images/collaboration.jpeg';
import PageLayout from 'components/common/PageLayout';
import { ENTERPRISE_CONTACT_LINK } from 'components/constants';

import styles from './Collaboration.module.scss';

const operatingLayers = [
  {
    number: '01',
    label: 'Organization',
    title: 'Set the standards everyone shares.',
    text: 'Centralize identity policies, brand systems, templates, products and organization-wide visibility in one operating layer.',
  },
  {
    number: '02',
    label: 'Groups',
    title: 'Give each team a focused workspace.',
    text: 'Separate work by department, business unit, market, brand, client or product portfolio, with a manager and access rules for each group.',
  },
  {
    number: '03',
    label: 'People',
    title: 'Match access to responsibility.',
    text: 'Decide who can create, edit, review, publish or access analytics. Add the same person to more than one group when work crosses team boundaries.',
  },
];

const workflow = [
  {
    title: 'Define the system',
    owner: 'Design',
    text: 'Create approved templates, reusable components and protected areas that carry the brand into every new product.',
  },
  {
    title: 'Create within clear boundaries',
    owner: 'Marketing and content',
    text: 'Start from an approved structure and update the content, fields and options assigned to the team.',
  },
  {
    title: 'Review the work',
    owner: 'Product and stakeholders',
    text: 'See who changed a product, review the latest version and keep feedback connected to the work before it moves forward.',
  },
  {
    title: 'Control publishing',
    owner: 'Authorized publishers',
    text: 'Limit publishing to the right roles while other collaborators continue creating, editing or reviewing.',
  },
  {
    title: 'Measure and improve',
    owner: 'Data and insights',
    text: 'Give analysts access to performance data and reports without giving them permission to change the product itself.',
  },
];

const controls = [
  {
    title: 'Role-based permissions',
    text: 'Set separate permissions for product creation, content editing, review, publishing, analytics and data access.',
  },
  {
    title: 'Group-based product access',
    text: 'Keep each team focused on the products and data assigned to its group, with separation where the organization needs it.',
  },
  {
    title: 'Shared template and product libraries',
    text: 'Make approved starting points easy to find and reuse across teams without rebuilding the same experience.',
  },
  {
    title: 'Revision history',
    text: 'Track product changes and restore an earlier version when a team needs to review or reverse an update.',
  },
  {
    title: 'Organization-wide analytics',
    text: 'Give authorized teams a unified view of product performance while maintaining access boundaries for individual groups.',
  },
  {
    title: 'Multiple sites and portfolios',
    text: 'Manage products for different sites, brands or portfolios inside one organization with distinct access rules.',
  },
  {
    title: 'Identity and account policies',
    text: 'Apply single sign-on, required two-factor authentication and account policies across the organization.',
  },
  {
    title: 'Product activity and ownership',
    text: 'See who is working on a product and keep responsibility visible as work moves between collaborators.',
  },
];

const teamResponsibilities = [
  {
    team: 'Design',
    responsibility:
      'Own templates, reusable components, responsive behavior and the parts of each product that stay protected.',
  },
  {
    team: 'Marketing and content',
    responsibility: 'Create products and update approved content without changing the underlying brand system.',
  },
  {
    team: 'Product and digital',
    responsibility:
      'Coordinate interaction logic, review, publishing and how each experience fits the wider customer journey.',
  },
  {
    team: 'Data and insights',
    responsibility:
      'Access analytics and reports, evaluate performance and work with the data destinations the organization has approved.',
  },
  {
    team: 'Organization admins',
    responsibility:
      'Manage groups, members, roles, identity requirements and access across the full product portfolio.',
  },
];

const Collaboration: React.FC = () => (
  <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>Collaboration and governance</p>
            <h1>Let every team build in one governed workspace.</h1>
            <p className={styles.heroText}>
              Structure Storycards around your departments, business units, brands or clients. Give each person the
              right access to create, edit, review, publish or analyze, while shared templates, products and revision
              history keep the work connected.
            </p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.statement}>
        <figure className={styles.statementVisual}>
          <img src={collaborationImage} alt="Storycards team members collaborating in one shared organization" />
        </figure>
        <div className={styles.sectionInner}>
          <h2>Your workspace should reflect the way the organization operates.</h2>
          <div>
            <p>
              A single flat workspace creates friction as more people, products and business units are added. Storycards
              gives the organization a shared operating layer, then separates day-to-day work into groups with their own
              members, products and access.
            </p>
            <p>
              A group can represent a department, market, brand, client or portfolio. People can belong to multiple
              groups, so cross-functional work stays connected without opening every product to everyone.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.modelSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>The operating model</p>
              <h2>Structure at the top. Focus for every team.</h2>
            </div>
            <p>
              Apply shared standards across the organization while each group sees the work, data and actions it is
              responsible for.
            </p>
          </header>
          <div className={styles.layerGrid}>
            {operatingLayers.map((layer) => (
              <article key={layer.title}>
                <div className={styles.layerMeta}>
                  <span>{layer.number}</span>
                  <em>{layer.label}</em>
                </div>
                <h3>{layer.title}</h3>
                <p>{layer.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.workflowSection}>
        <div className={styles.sectionInner}>
          <header className={styles.workflowHeader}>
            <p className={styles.eyebrow}>A connected workflow</p>
            <h2>Clear ownership from the first template to the next improvement.</h2>
            <p>
              Storycards keeps design, content, product, publishing and measurement in one workflow while giving each
              function the level of access it needs.
            </p>
          </header>
          <div className={styles.workflowList}>
            {workflow.map((step, index) => (
              <article key={step.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <small>{step.owner}</small>
                  <h3>{step.title}</h3>
                </div>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.teamsSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>Built for cross-functional work</p>
              <h2>One product can involve many teams without blurring responsibility.</h2>
            </div>
            <p>
              Each function works on the same product from its own role. The organization keeps one source of truth
              instead of passing files and decisions between disconnected tools.
            </p>
          </header>
          <div className={styles.responsibilityTable}>
            {teamResponsibilities.map((item, index) => (
              <article key={item.team}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.team}</h3>
                <p>{item.responsibility}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.controlsSection}>
        <div className={styles.sectionInner}>
          <header className={styles.controlsHeader}>
            <p className={styles.eyebrow}>Enterprise collaboration controls</p>
            <h2>Give people access to what they need, and no more.</h2>
            <p>
              The collaboration layer covers product access, reusable assets, account policies, publishing and
              performance visibility across the organization.
            </p>
          </header>
          <div className={styles.controlsGrid}>
            {controls.map((control, index) => (
              <article key={control.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{control.title}</h3>
                <p>{control.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.serviceSection}>
        <div className={styles.sectionInner}>
          <div className={styles.serviceCopy}>
            <p className={styles.eyebrow}>Adoption across the organization</p>
            <h2>A working model your teams can use from day one.</h2>
          </div>
          <div className={styles.serviceDetails}>
            <article>
              <span>01</span>
              <h3>Structured onboarding</h3>
              <p>
                Map groups, roles, templates and publishing responsibilities to the way your organization already works.
              </p>
            </article>
            <article>
              <span>02</span>
              <h3>Training and ongoing support</h3>
              <p>
                Help designers, creators, analysts and administrators learn the workflow that applies to their role.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2>Design a Storycards workspace around your organization.</h2>
          <div>
            <p>
              Talk with our enterprise team about your departments, product portfolios, access model and publishing
              workflow.
            </p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  </PageLayout>
);

export default Collaboration;
